<template>
  <v-container>
    <!-- Search Bar -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="4">
        <v-form @submit.prevent="handleSearch">
          <div class="d-flex align-center">
            <v-text-field
              v-model="searchId"
              label="Sale ID"
              hide-details
              density="compact"
              class="mr-2"
              @keyup.enter="handleSearch"
            ></v-text-field>
            <v-btn
              color="primary"
              :loading="searching"
              @click="handleSearch"
            >
              Quick Search
            </v-btn>
          </div>
        </v-form>
      </v-col>
    </v-row>

    <v-row v-if="sale">
      <v-col cols="12">
        <h1 class="text-h3 mb-4">Sale Details {{ sale?.ID ? `#${sale.ID}` : '' }}</h1>

        <!-- Loading State -->
        <v-card v-if="loading" class="pa-4 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <div class="text-h6 mt-4">Loading sale...</div>
        </v-card>

        <!-- Error State -->
        <v-alert
          v-else-if="error"
          type="error"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- Sale Form -->
        <sale-form
          v-if="sale"
          :sale="sale"
          :loading="saving"
          @save="handleSave"
          @cancel="handleCancel"
          @send-message="handleSendMessage"
          @submit="handleSubmit"
          @unsubmit="handleUnsubmit"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { salesService } from '@/services/salesService'
import SaleForm from '@/components/sales/SaleForm.vue'
import { useAuthStore } from '@/stores/authStore' 

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const sale = ref(null)
const loading = ref(false)
const saving = ref(false)
const error = ref(null)
const searching = ref(false)
const searchId = ref(route.params.saleId || '')

// Fetch sale details
const fetchSale = async (id) => {
  if (!id) return
  
  loading.value = true
  error.value = null

  try {
    const response = await salesService.getSale(id)
    sale.value = response
  } catch (err) {
    console.error('Error fetching sale:', err)
    error.value = 'Failed to load sale details'
  } finally {
    loading.value = false
  }
}

const handleSearch = async () => {
  if (!searchId.value) return
  
  searching.value = true
  try {
    // Update route
    await router.push({
      name: 'SalePage',
      params: { saleId: searchId.value }
    })
  } finally {
    searching.value = false
  }
}

// Watch for route param changes
watch(() => route.params.saleId, (newId) => {
  if (newId) {
    searchId.value = newId
    fetchSale(newId)
  } else {
    sale.value = null
  }
}, { immediate: true })

const handleSave = async (updatedSale) => {
  saving.value = true
  try {
    await salesService.updateSale(updatedSale.ID, updatedSale)
    sale.value = updatedSale
  } catch (err) {
    error.value = 'Failed to save changes'
    console.error('Error updating sale:', err)
  } finally {
    saving.value = false
  }
}

const handleCancel = async () => {
  saving.value = true
  try {
    await salesService.cancelSale(sale.value.ID)
    await fetchSale()
  } catch (err) {
    error.value = 'Failed to cancel sale'
    console.error('Error canceling sale:', err)
  } finally {
    saving.value = false
  }
}

const handleSubmit = async () => {
  saving.value = true
  try {
    sale.value.submitted = 'Manual'
    sale.value.submitted_by = authStore.user.username
    debugger;
    await salesService.updateSale(sale.value.ID, sale.value)
    await fetchSale()
  } catch (err) {
    error.value = 'Failed to submit sale'
    console.error('Error submitting sale:', err)
  } finally {
    saving.value = false
  }
}

const handleUnsubmit = async () => {
  saving.value = true
  try {
    sale.value.submitted = ''
    await salesService.updateSale(sale.value.ID, sale.value)
    await fetchSale()
  } catch (err) {
    error.value = 'Failed to unsubmit sale'
    console.error('Error unsubmitting sale:', err)
  } finally {
    saving.value = false
  }
}

const handleSendMessage = () => {
  router.push(`/sales/${sale.value.ID}/message`)
}
</script> 